import { Drawer } from "@smartfaster-ui/components";
import { type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInteriorDesigner } from "../context/InteriorDesignerContext";
import type { PanelId } from "../context/types";
import { PanelFooterContext } from "./PanelFooterContext";
import { FurnishingPanel } from "./FurnishingPanel";
import { HistoryPanel } from "./HistoryPanel";
import { InteriorFinishingPanel } from "./InteriorFinishingPanel";
import { WallColorPanel } from "./WallColorPanel";
import { WishesAndSuggestionsPanel } from "./WishesAndSuggestionsPanel";

const PANEL_TITLE_KEYS: Record<PanelId, string> = {
  interiorFinishing: "interiorDesigner.interiorFinishing.title",
  furnishing: "interiorDesigner.furnishing.title",
  wallColor: "interiorDesigner.wallColor.title",
  otherWishes: "interiorDesigner.wishesAndSuggestions.title",
  history: "interiorDesigner.history.title",
};

function PanelContent({ activePanel }: { activePanel: PanelId }) {
  switch (activePanel) {
    case "interiorFinishing":
      return <InteriorFinishingPanel />;
    case "furnishing":
      return <FurnishingPanel />;
    case "wallColor":
      return <WallColorPanel />;
    case "otherWishes":
      return <WishesAndSuggestionsPanel />;
    case "history":
      return <HistoryPanel />;
  }
}

export function ActivePanel() {
  const { t } = useTranslation();
  const { state, dispatch } = useInteriorDesigner();
  const lastPanelRef = useRef<PanelId>("interiorFinishing");

  if (state.activePanel) {
    lastPanelRef.current = state.activePanel;
  }

  const panelToRender = state.activePanel ?? lastPanelRef.current;
  const isOpen = state.activePanel !== null;

  const [footer, setFooter] = useState<ReactNode>(null);

  return (
    <PanelFooterContext.Provider value={setFooter}>
      <Drawer
        open={isOpen}
        onClose={() => dispatch({ type: "PANEL_CLOSED" })}
        keepMounted
        hideScrollbar
        title={t(PANEL_TITLE_KEYS[panelToRender])}
        placement="end"
        size="xl"
        marginRight="xs"
        closable
        trailing={footer}
      >
        <PanelContent activePanel={panelToRender} />
      </Drawer>
    </PanelFooterContext.Provider>
  );
}
